import React, { useEffect, useState } from "react";
import {
  App,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Input,
  InputNumber,
  Radio,
  Select,
  Tag,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { FiPlus, FiTrash2, FiPlusCircle } from "react-icons/fi";
import AppControl from "../../components/";
import Card from "../../components/controls/Card";
import {
  saveFormElement,
  updateFormElement,
  getFormElement,
} from "../../store/slice/formElements";

const CONTROL_TYPES = [
  { label: "Auto Complete", value: "autocomplete" },
  { label: "Avatar", value: "avatar" },
  { label: "Button", value: "button" },
  { label: "Checkbox", value: "checkbox" },
  { label: "Date", value: "date" },
  { label: "Image Upload", value: "image" },
  { label: "Multi Select", value: "multiselect" },
  { label: "Owner", value: "owner" },
  { label: "Password", value: "password" },
  { label: "Radio", value: "radio" },
  { label: "Select", value: "select" },
  { label: "Select with Search", value: "selectwithsearch" },
  { label: "Switch", value: "switch" },
  { label: "Table", value: "table" },
  { label: "Text", value: "text" },
  { label: "Tree", value: "tree" },
];

const VALIDATION_TYPES = [
  { label: "String", value: "string" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
  { label: "Array", value: "array" },
  { label: "Boolean", value: "boolean" },
  { label: "Mixed", value: "mixed" },
];

const VALIDATION_RULES = [
  { label: "Required", value: "required" },
  { label: "Min", value: "min" },
  { label: "Max", value: "max" },
  { label: "Email", value: "email" },
  { label: "URL", value: "url" },
  { label: "Matches (Regex)", value: "matches" },
  { label: "One Of", value: "oneOf" },
  { label: "Not One Of", value: "notOneOf" },
  { label: "Nullable", value: "nullable" },
  { label: "Positive", value: "positive" },
  { label: "Negative", value: "negative" },
  { label: "Integer", value: "integer" },
];

const emptyValidation = { type: "", params: [""] };

const emptyField = {
  fieldId: "",
  label: "",
  type: "text",
  placeholder: "",
  defaultValue: "",
  options: "",
  validationType: "string",
  validations: [],
  order: 0,
  visible: true,
  enable: true,
  colspan: 1,
  props: "",
};

const validationSchema = Yup.object().shape({
  applicationName: Yup.string().required("Application name is required"),
  formName: Yup.string().required("Form name is required"),
  fields: Yup.array().of(
    Yup.object().shape({
      fieldId: Yup.string().required("Field ID is required"),
      label: Yup.string().required("Label is required"),
      type: Yup.string().required("Control type is required"),
    }),
  ),
});

function FieldPanel({ field, index, formik }) {
  const fieldPath = `fields[${index}]`;
  const set = (key, val) => formik.setFieldValue(`${fieldPath}.${key}`, val);

  return (
    <div>
      <div className="grid md:grid-cols-3 sm:grid-cols-1 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Field ID <span className="text-red-500">*</span>
          </label>
          <Input
            value={field.fieldId}
            placeholder="e.g. branchName"
            onChange={(e) => set("fieldId", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Label <span className="text-red-500">*</span>
          </label>
          <Input
            value={field.label}
            placeholder="e.g. Branch Name"
            onChange={(e) => set("label", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Control Type <span className="text-red-500">*</span>
          </label>
          <Select
            className="w-full"
            value={field.type}
            options={CONTROL_TYPES}
            onChange={(val) => set("type", val)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Placeholder
          </label>
          <Input
            value={field.placeholder}
            placeholder="Enter placeholder text"
            onChange={(e) => set("placeholder", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Default Value
          </label>
          <Input
            value={field.defaultValue}
            placeholder="Enter default value"
            onChange={(e) => set("defaultValue", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Options (JSON string)
          </label>
          <Input
            value={field.options}
            placeholder='[{"label":"Yes","value":"1"}]'
            onChange={(e) => set("options", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Validation Type
          </label>
          <Select
            className="w-full"
            value={field.validationType}
            options={VALIDATION_TYPES}
            onChange={(val) => set("validationType", val)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Order
          </label>
          <InputNumber
            className="w-full"
            value={field.order}
            min={0}
            onChange={(val) => set("order", val ?? 0)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Column Span
          </label>
          <InputNumber
            className="w-full"
            value={field.colspan}
            min={1}
            max={12}
            onChange={(val) => set("colspan", val ?? 1)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Extra Props (JSON string)
          </label>
          <Input
            value={field.props}
            placeholder='{"size":"large"}'
            onChange={(e) => set("props", e.target.value)}
          />
        </div>
        <div className="flex gap-4 items-center pt-5">
          <Checkbox
            checked={field.visible}
            onChange={(e) => set("visible", e.target.checked)}
          >
            Visible
          </Checkbox>
          <Checkbox
            checked={field.enable}
            onChange={(e) => set("enable", e.target.checked)}
          >
            Enabled
          </Checkbox>
        </div>
      </div>

      <Divider titlePlacement="left" plain>
        <span className="text-sm font-medium text-gray-600">
          Validation Rules
        </span>
      </Divider>

      <FieldArray name={`${fieldPath}.validations`}>
        {({ push: pushValidation, remove: removeValidation }) => (
          <div className="space-y-2">
            {field.validations.map((validation, vIndex) => (
              <div key={vIndex} className="flex gap-2 items-center">
                <div className="w-44">
                  <Select
                    className="w-full"
                    placeholder="Rule type"
                    value={validation.type || undefined}
                    options={VALIDATION_RULES}
                    onChange={(val) =>
                      formik.setFieldValue(
                        `${fieldPath}.validations[${vIndex}].type`,
                        val,
                      )
                    }
                  />
                </div>
                <div className="flex-1">
                  <Input
                    value={
                      Array.isArray(validation.params)
                        ? validation.params.join(", ")
                        : ""
                    }
                    placeholder="Params (comma-separated, e.g. 3, Min 3 chars)"
                    onChange={(e) => {
                      const raw = e.target.value;
                      const params = raw
                        ? raw.split(",").map((p) => p.trim())
                        : [""];
                      formik.setFieldValue(
                        `${fieldPath}.validations[${vIndex}].params`,
                        params,
                      );
                    }}
                  />
                </div>
                <Button
                  danger
                  size="small"
                  icon={<FiTrash2 />}
                  onClick={() => removeValidation(vIndex)}
                />
              </div>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<FiPlusCircle className="inline mr-1" />}
              onClick={() => pushValidation({ ...emptyValidation })}
            >
              Add Validation Rule
            </Button>
          </div>
        )}
      </FieldArray>
    </div>
  );
}

function FormElementsAddEdit() {
  const [title, setTitle] = useState("CREATE FORM ELEMENT");
  const [activeKeys, setActiveKeys] = useState([]);
  let { id } = useParams();
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formElement, isLoading } = useSelector((state) => state.formElements);

  useEffect(() => {
    if (id) {
      setTitle("EDIT FORM ELEMENT");
      dispatch(getFormElement(id));
    }
  }, [id]);

  const initialValues =
    id && formElement
      ? {
          applicationName: formElement.applicationName || "",
          formName: formElement.formName || "",
          fields: formElement.fields || [],
        }
      : { applicationName: "", formName: "", fields: [] };

  const onSubmit = async (values) => {
    if (id) {
      const resultAction = await dispatch(updateFormElement({ ...values, id }));
      if (updateFormElement.fulfilled.match(resultAction)) {
        message.success(resultAction.payload.message);
        navigate("/form-elements");
      } else {
        message.error(`Update failed: ${resultAction.error.message}`);
      }
    } else {
      const resultAction = await dispatch(saveFormElement(values));
      if (saveFormElement.fulfilled.match(resultAction)) {
        message.success(resultAction.payload.message);
        navigate("/form-elements");
      } else {
        message.error(`Save failed: ${resultAction.error.message}`);
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Card title={title}>
          <Form>
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application Name <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("applicationName")}
                  placeholder="e.g. Fixilo"
                  status={
                    formik.touched.applicationName &&
                    formik.errors.applicationName
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.applicationName &&
                  formik.errors.applicationName && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.applicationName}
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Name <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("formName")}
                  placeholder="e.g. BranchMaster"
                  status={
                    formik.touched.formName && formik.errors.formName
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.formName && formik.errors.formName && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.formName}
                  </p>
                )}
              </div>
            </div>

            {/* Fields Section */}
            <FieldArray name="fields">
              {({ push, remove }) => (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h6 className="text-base font-semibold text-gray-800">
                      Form Fields{" "}
                      <Tag color="blue">{formik.values.fields.length}</Tag>
                    </h6>
                    <Button
                      type="dashed"
                      icon={<FiPlus className="inline mr-1" />}
                      onClick={() => {
                        const newIndex = String(formik.values.fields.length);
                        push({ ...emptyField });
                        setActiveKeys((prev) => [...prev, newIndex]);
                      }}
                    >
                      Add Field
                    </Button>
                  </div>

                  {formik.values.fields.length === 0 && (
                    <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                      No fields yet. Click "Add Field" to start building your
                      form.
                    </div>
                  )}

                  <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(keys)}
                    items={formik.values.fields
                      .map((field, index) => ({ field, index }))
                      .sort(
                        (a, b) => (a.field.order ?? 0) - (b.field.order ?? 0),
                      )
                      .map(({ field, index }) => ({
                        key: String(index),
                        label: (
                          <span className="font-medium">
                            {field.fieldId || `Field ${index + 1}`}
                            {field.label && (
                              <span className="text-gray-500 ml-2">
                                — {field.label}
                              </span>
                            )}
                            <Tag className="ml-2" color="orange">
                              {field.type}
                            </Tag>
                          </span>
                        ),
                        extra: (
                          <Button
                            size="small"
                            danger
                            icon={<FiTrash2 className="inline" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(index);
                              setActiveKeys((prev) =>
                                prev
                                  .filter((k) => k !== String(index))
                                  .map((k) =>
                                    Number(k) > index
                                      ? String(Number(k) - 1)
                                      : k,
                                  ),
                              );
                            }}
                          >
                            Remove
                          </Button>
                        ),
                        children: (
                          <FieldPanel
                            field={field}
                            index={index}
                            formik={formik}
                          />
                        ),
                      }))}
                  />
                </div>
              )}
            </FieldArray>

            {/* Action Buttons */}
            <div className="border-t-2 border-neutral-100 pt-4 mt-6">
              <AppControl
                control="button"
                type="submit"
                name="Save"
                size="md"
                color="orange"
                disabled={!formik.isValid || isLoading}
                loading={isLoading}
              />
              <AppControl
                control="button"
                type="button"
                name="Cancel"
                size="md"
                color="blue"
                onClick={() => navigate("/form-elements")}
              />
            </div>
          </Form>
        </Card>
      )}
    </Formik>
  );
}

export default FormElementsAddEdit;
